/**
 * Section group detection — finds multi-item card / stat / flex groups
 * inside a section's authored HTML so the property panel can offer a
 * "Columns" override.
 *
 * Match rule:
 *   - Element has a class matching /^cs-[a-z0-9-]+-group$/ (catches
 *     cs-card-group, cs-stat-group, cs-flex-group, cs-pricing-group, etc.).
 *     Tokenized against `classList` so multi-class elements like
 *     `class="cs-card-group cs-fade-in"` qualify; a whole-string regex
 *     would miss them.
 *   - The element has ≥ 2 children with class `cs-item`. Single-item
 *     groups don't need a column control.
 *
 * Returns the first qualifying group (groupSelector + itemCount) or null.
 * Callers gate the "Columns" drawer row on a non-null return.
 */

const GROUP_CLASS_RE = /^cs-[a-z0-9-]+-group$/;

/**
 * @param {{ html?: string }} section
 * @returns {{ groupSelector: string, itemCount: number } | null}
 */
export function detectMultiItemGroups(section) {
  if (!section || typeof section.html !== 'string' || !section.html) return null;
  if (typeof DOMParser === 'undefined') return null;

  let doc;
  try {
    doc = new DOMParser().parseFromString(`<div>${section.html}</div>`, 'text/html');
  } catch {
    return null;
  }
  const root = doc?.body?.firstElementChild;
  if (!root) return null;

  for (const el of root.querySelectorAll('*')) {
    const classList = el.classList;
    if (!classList || classList.length === 0) continue;

    let matchedClass = null;
    for (const token of classList) {
      if (GROUP_CLASS_RE.test(token)) {
        matchedClass = token;
        break;
      }
    }
    if (!matchedClass) continue;

    let itemCount = 0;
    for (const child of el.children) {
      if (child.classList && child.classList.contains('cs-item')) itemCount += 1;
    }
    if (itemCount < 2) continue;

    return { groupSelector: `.${matchedClass}`, itemCount };
  }

  return null;
}
